import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes as RouterRoutes, Route } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import ProtectedRoute from "components/ProtectedRoute";
import AuthRedirect from "components/AuthRedirect";
import LoadingFallback from "components/LoadingFallback";
import GTMTracker from "./components/GTMTracker";

// Lazy load all page components for automatic code splitting
// Public pages - load immediately for better UX
const ServiceLandingHub = lazy(() => import("pages/service-landing-hub"));
const LeadPageViewer = lazy(() => import("pages/public/LeadPageViewer"));
const AIAutomation = lazy(() => import("pages/ai-automation"));
const AboutUs = lazy(() => import("pages/public/AboutUs"));
const Contact = lazy(() => import("pages/public/Contact"));
const Careers = lazy(() => import("pages/public/Careers"));
const Blog = lazy(() => import("pages/public/Blog"));
const PrivacyPolicy = lazy(() => import("pages/public/PrivacyPolicy"));
const TermsOfUse = lazy(() => import("pages/public/TermsOfUse"));

// Auth pages - lazy load
const Login = lazy(() => import("pages/auth/Login"));
const Register = lazy(() => import("pages/auth/Register"));
const ForgotPassword = lazy(() => import("pages/auth/ForgotPassword"));
const RegistrationSuccess = lazy(() => import("pages/auth/RegistrationSuccess"));
const EmailVerificationPending = lazy(() => import("pages/auth/EmailVerificationPending"));
const PendingApproval = lazy(() => import("pages/auth/PendingApproval"));
const Unauthorized = lazy(() => import("pages/Unauthorized"));
const NotFound = lazy(() => import("pages/NotFound"));

// Protected pages - lazy load
const Profile = lazy(() => import("pages/Profile"));
const Notifications = lazy(() => import("pages/Notifications"));

// Client pages
const ClientDashboard = lazy(() => import("pages/client-dashboard"));
const ClientProjectDashboard = lazy(() => import("pages/client-project-dashboard"));
const ClientServiceRequestCenter = lazy(() => import("pages/client-service-request-center"));
const ClientInvoices = lazy(() => import("pages/client-invoices"));

// Employee pages
const EmployeeDashboard = lazy(() => import("pages/employee-dashboard"));
const EmployeeProjectManagementHub = lazy(() => import("pages/employee-project-management-hub"));
const MarketingWorkspace = lazy(() => import("pages/employee-marketing"));
const DeveloperWorkspace = lazy(() => import("pages/employee-dev"));

// Admin pages
const AdminDashboard = lazy(() => import("pages/admin-dashboard"));
const AdminAnalyticsCommandCenter = lazy(() => import("pages/admin-analytics-command-center"));
const AdminUsers = lazy(() => import("pages/admin-users"));
const AdminRequests = lazy(() => import("pages/admin-requests"));
const MarketingManagement = lazy(() => import("pages/marketing-management"));
const Routes = () => {
  return (
    <BrowserRouter>
      <GTMTracker />
      <ErrorBoundary>
        <ScrollToTop />
        <Suspense fallback={<LoadingFallback />}>
          <RouterRoutes>
            {/* Public Routes */}
            <Route path="/" element={<ServiceLandingHub />} />
            <Route path="/service-landing-hub" element={<ServiceLandingHub />} />
            <Route path="/ai-automation" element={<AIAutomation />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-use" element={<TermsOfUse />} />
            <Route path="/l/:slug" element={<LeadPageViewer />} />
            <Route path="/dashboard" element={<AuthRedirect />} />

            {/* Authentication Routes */}
            <Route path="/auth/login" element={<Login />} />
            <Route path="/auth/register" element={<Register />} />
            <Route path="/auth/forgot-password" element={<ForgotPassword />} />
            <Route path="/auth/registration-success" element={<RegistrationSuccess />} />
            <Route path="/auth/verify-email" element={<EmailVerificationPending />} />
            <Route path="/auth/pending-approval" element={<PendingApproval />} />
            <Route path="/unauthorized" element={<Unauthorized />} />

            {/* Profile and Settings Routes */}
            <Route path="/profile" element={
              <ProtectedRoute
                element={<Profile />}
                allowedRoles={['client', 'employee', 'project_manager', 'admin']}
              />
            } />
            <Route path="/notifications" element={
              <ProtectedRoute
                element={<Notifications />}
                allowedRoles={['client', 'employee', 'project_manager', 'admin']}
              />
            } />

            {/* Protected Routes - Client */}
            <Route path="/client-dashboard" element={
              <ProtectedRoute
                element={<ClientDashboard />}
                allowedCategories={['client']}
                allowedRoles={['admin', 'client']}
              />
            } />
            <Route path="/client-project-dashboard" element={
              <ProtectedRoute
                element={<ClientProjectDashboard />}
                allowedCategories={['client']}
                allowedRoles={['admin', 'client']}
              />
            } />
            <Route path="/client-service-request-center" element={
              <ProtectedRoute
                element={<ClientServiceRequestCenter />}
                allowedCategories={['client']}
                allowedRoles={['employee', 'project_manager', 'admin', 'client']}
              />
            } />
            <Route path="/client-invoices" element={
              <ProtectedRoute
                element={<ClientInvoices />}
                allowedCategories={['client']}
                allowedRoles={['admin', 'client']}
              />
            } />

            {/* Protected Routes - Employee */}
            <Route path="/employee-dashboard" element={
              <ProtectedRoute
                element={<EmployeeDashboard />}
                allowedCategories={['employee']}
              />
            } />
            <Route path="/employee-project-management-hub" element={
              <ProtectedRoute
                element={<EmployeeProjectManagementHub />}
                allowedRoles={['project_manager', 'admin']}
              />
            } />
            <Route path="/employee/marketing" element={
              <ProtectedRoute
                element={<MarketingWorkspace />}
                allowedCategories={['employee']}
                allowedPages={['market_tools']}
              />
            } />
            <Route path="/employee/dev" element={
              <ProtectedRoute
                element={<DeveloperWorkspace />}
                allowedCategories={['employee']}
                allowedPages={['dev_tools']}
              />
            } />

            {/* Protected Routes - Admin */}
            <Route path="/admin-dashboard" element={
              <ProtectedRoute
                element={<AdminDashboard />}
                allowedCategories={['admin']}
                allowedRoles={['admin']}
              />
            } />
            <Route path="/admin-analytics-command-center" element={
              <ProtectedRoute
                element={<AdminAnalyticsCommandCenter />}
                allowedCategories={['admin']}
                allowedRoles={['admin']}
              />
            } />
            <Route path="/marketing-management" element={
              <ProtectedRoute
                element={<MarketingManagement />}
                allowedRoles={['admin', 'marketing_manager']}
              />
            } />
            <Route path="/admin/users" element={
              <ProtectedRoute
                element={<AdminUsers />}
                allowedCategories={['admin']}
                allowedRoles={['admin']}
              />
            } />
            <Route path="/admin/requests" element={
              <ProtectedRoute
                element={<AdminRequests />}
                allowedCategories={['admin']}
                allowedRoles={['admin', 'project_manager']}
              />
            } />

            {/* 404 Route */}
            <Route path="*" element={<NotFound />} />
          </RouterRoutes>
        </Suspense>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;